
import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface GalleryItem {
  id: number;
  title: string;
  image: string;
  description?: string;
  views?: number;
  treatmentType?: string;
}

interface DoctorBeforeAfterGalleryProps {
  gallery: GalleryItem[];
  doctorId?: string | number;
}

const DoctorBeforeAfterGallery: React.FC<DoctorBeforeAfterGalleryProps> = ({ gallery, doctorId = "1" }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ 
        left: -280, 
        behavior: 'smooth' 
      });
    }
  };
  
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ 
        left: 280, 
        behavior: 'smooth' 
      });
    }
  };

  const handleViewAll = () => {
    navigate('/doctor-area/before-after');
  };

  if (!gallery || gallery.length === 0) return null;

  return (
    <div className="relative group">
      <div 
        ref={scrollContainerRef}
        className="flex overflow-x-auto gap-4 py-2 scrollbar-none snap-x snap-mandatory"
      >
        {gallery.map((item) => (
          <div 
            key={item.id} 
            className="min-w-[280px] flex-none snap-start rounded-lg overflow-hidden border bg-white hover:shadow-md transition-shadow duration-200"
          >
            <div className="relative w-full h-48">
              <img 
                src={item.image} 
                alt={item.title} 
                className="w-full h-full object-cover"
              />
              {item.treatmentType && (
                <div className="absolute top-2 left-2 bg-primary/80 text-white text-xs px-2 py-1 rounded">
                  {item.treatmentType}
                </div>
              )}
            </div>
            <div className="p-3">
              <h3 className="font-medium">{item.title}</h3>
              {item.description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{item.description}</p>
              )}
              {item.views && (
                <p className="text-xs text-muted-foreground mt-2">{item.views} views</p>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Scroll Navigation Buttons */}
      <Button 
        variant="outline" 
        size="icon"
        className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={scrollLeft}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      
      <Button 
        variant="outline" 
        size="icon"
        className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={scrollRight}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      
      {/* View All Button */}
      <Button
        variant="outline"
        size="sm"
        className="absolute top-0 right-0 text-xs"
        onClick={handleViewAll}
      >
        View All
      </Button>
    </div>
  );
};

export default DoctorBeforeAfterGallery;
